<script lang="ts" module>
  declare var Stripe: any
</script>

<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { formatter, xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  let props = $props()
  let {
    onpurchase
  }: { onpurchase: (alignment_test: AlignmentTestType) => void } = props

  let stripe_publish_key: string = $state("")
  let stripe_product: string = $state("")
  let stripe_price: string = $state("")

  let productPrice: number = $state(0)
  let discountAmount: number = $state(0)
  let totalAmount: number = $state(0)

  let isLoading = $state(true)
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/alignment_test_purchase/alignment_test_data.json`
      )

      let data = resp.data

      stripe_product = data.stripe_product
      stripe_publish_key = data.publish_key

      productPrice = stripe_product.unit_amount / 100
      totalAmount = productPrice

      mounted = true
    } catch (e) {
      console.log(e)
      toast(
        "There was an error loading the alignment test purchase data",
        "error"
      )
    } finally {
      isLoading = false
    }
  }

  const hostname = location.hostname.substring(
    0,
    location.hostname.indexOf(".")
  )

  let can_purchase = hostname !== "teams"

  let errors: any = $state({})

  let test_image = new URL(`/images/alignment_test/test.jpg`, import.meta.url)
    .href

  let form: HTMLFormElement = $state(null)
  let stripe_token: string = $state("")
  let cardErrors: any = $state(null)
  let cardTokenInput: HTMLFormElement = $state(null)
  let saving = $state(false)
  let mounted = false
  let auth_token: string
  /**
   * Submit data
   */
  async function handleSubmit(e): Promise<void> {
    saving = true
    const formData = new FormData(e.target)

    // Attempt to tokenize the card number
    try {
      let result: any = { token: { id: "" } }
      if (totalAmount > 0) {
        result = await stripe.createToken(cardElement)
      }

      if (result.error) {
        // On error, display the error message.
        cardErrors.classList.remove("hidden")
        cardErrors.textContent = result.error.message
        throw new Error("Card error")
      } else {
        // On success, add the token to the hidden field and submit to the server
        stripe_token = result.token.id

        const data = {}
        for (let field of formData) {
          const [key, value] = field
          data[key] = value
        }
        data["stripe_token"] = stripe_token
        const resp = await xhr.post(
          `/plans/${appStore.plan.id}/alignment_test_purchase.json`,
          {
            purchase: data
          }
        )
        console.log(resp.data)
        let alignment_test = resp.data
        onpurchase(alignment_test)
      }
    } catch (e) {
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  let stripe: any
  let cardElement: any

  const clearCardError = () => {
    cardErrors.classList.add("hidden")
  }

  /**
   * Stripe
   */
  function loadStripeElements() {
    console.log("STRIPE PUBLISH KEY", stripe_publish_key)

    // Time for Stripe.js to do its magic.
    stripe = Stripe(stripe_publish_key)
    let elements = stripe.elements()

    let style = {
      base: {
        fontSize: "16px",
        color: "#32325d",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#a0aec0"
        }
      }
    }

    cardElement = elements.create("card", { style: style })
    cardElement.mount("#card-element")

    cardElement.on("change", clearCardError)
  }

  let validate_btn: HTMLFormElement = $state(null)
  let stripe_coupon: string = $state("")
  let validating = $state(false)
  let checked_coupon = $state(false)

  let coupon = $state({})

  /**
   * Review valid coupon
   */
  async function checkCoupon(): Promise<void> {
    stripe_coupon = stripe_coupon?.trim()

    if (stripe_coupon?.length > 0) {
      console.log("validating")
      checked_coupon = true
      validating = true
      console.log(stripe_coupon.trim())

      try {
        const resp = await xhr.get(
          `/stripe/promotion_code/${stripe_coupon.trim()}`
        )
        let promotion_code = resp.data

        if (promotion_code.valid) {
          coupon = promotion_code.coupon
          discountAmount = (productPrice * (coupon.percent_off || 0)) / 100
          totalAmount = productPrice - discountAmount
        } else {
          coupon.valid = false
        }
      } catch (e) {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
        toast(
          "There was an error validating the coupon code try again",
          "error"
        )
      } finally {
        validating = false
      }
    } else {
      coupon = {}
    }
  }

  function focusCoupon() {
    validate_btn.classList.remove("btn-secondary")
    validate_btn.classList.add("btn-primary")
  }

  onMount(async () => {
    stripe_coupon && !checked_coupon && checkCoupon()

    initialize()
    auth_token = (<HTMLMetaElement>(
      document.querySelector("meta[name=csrf-token]")
    )).content
  })

  function stripeLoaded(): void {
    // The external Stripe javascript is ready.
    // stripeReady = true
    const interval = setInterval(() => {
      if (can_purchase && mounted) {
        loadStripeElements()
        clearInterval(interval)
      }
    }, 500)
  }
</script>

<svelte:head>
  <script src="https://js.stripe.com/v3/" onload={stripeLoaded}></script>
</svelte:head>

<div class="grid grid-cols-2 gap-4">
  <div>
    <div
      class="relative rounded-lg shadow-xl overflow-hidden bg-gray-800 opacity-60"
    >
      <img
        src={test_image}
        class="absolute inset-0 h-full w-full object-cover"
        alt="Alignment Test"
      />
      <div class="relative px-4">
        <div class="mt-8">
          <div
            class="my-12 relative py-8 px-4 rounded justify-center bg-black opacity-90"
          >
            <p
              class="text-center text-2xl lg:text-3xl font-medium text-white font-medium"
            >
              Alignment Test
            </p>
            <p class="text-base mt-4 text-white">
              This purchase includes both a Pre-Alignment Test and
              Post-Alignment Tests for all your plan members, plus any
              additional non-member participants you would like.
            </p>
            <p class="text-base text-white mt-4">
              The Pre-Alignment Test is designed to be taken by your team before
              engaging the Lead With Purpose process. Post-Alignment Test is
              designed to be taken by your team at the completion of the Lead
              With Purpose process.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div>
    <div class="space-y-2 mt-4">
      <h2 class="text-3xl font-bold text-center text-gray-900 mb-6">
        Purchase Alignment Test
      </h2>

      {#if isLoading}
        <div class="py-12 flex justify-center w-full">
          <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
        </div>
      {:else if can_purchase}
        <h3 class="text-xl font-bold tracking-tight mt-4 text-gray-600">
          Purchase Details
        </h3>

        <div class="mt-3 flex justify-between py-2">
          <div class="text-left text-lg">
            <p>Alignment Test</p>
          </div>
          <div class="text-right text-lg font-bold">
            <p>{formatter.format(productPrice)} USD</p>
          </div>
        </div>

        {#if discountAmount}
          <div class="mt-4 flex justify-between py-2">
            <div class="text-left text-base italic text-grey-700">
              <p>Discount</p>
            </div>
            <div class="text-right text-base italic text-gray-700 font-bold">
              <p>-{formatter.format(discountAmount)}</p>
            </div>
          </div>
        {/if}
        <div
          class="mt-6 mb-4 py-2 flex justify-between border-t border-dotted border-gray-400"
        >
          <div class="text-left text-xl">
            <p>Today's Total</p>
          </div>
          <div class="text-right text-xl font-bold">
            <p>{formatter.format(totalAmount)} USD</p>
          </div>
        </div>

        <div class="mt-4 grid grid-cols-1 gap-y-4 gap-x-4">
          <div class="border-t pt-2">
            <div class="flex coupon-label">
              <label for="coupon">Coupon </label>
              {#if coupon.valid === true}
                <div class="text-green-600 text-right w-full">Valid Coupon</div>
              {:else if coupon.valid === false}
                <div class="text-red-600 text-right w-full">Invalid Coupon</div>
              {/if}
            </div>
            <div class="flex mt-1">
              <input
                type="text"
                name="subscription[stripe_coupon]"
                bind:value={stripe_coupon}
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md rounded-r-none px-2 py-1"
                class:border-red-600={coupon.valid === false}
                class:border-green-600={coupon.valid === true}
                placeholder="Coupon code"
                onblur={checkCoupon}
                onfocus={focusCoupon}
              />
              <button
                onclick={(e) => {
                  e.preventDefault()
                  checkCoupon()
                }}
                bind:this={validate_btn}
                class="btn btn-secondary whitespace-nowrap !rounded-l-none"
                disabled={validating}
              >
                Validate Coupon
              </button>
            </div>
          </div>
        </div>

        <form
          onsubmit={(e) => {
            e.preventDefault()
            handleSubmit(e)
          }}
          bind:this={form}
          id="payment-form"
        >
          {#if Object.keys(errors).length > 0}
            <div class="bg-red-600 text-white text-sm p-3 mb-2">
              There are some errors in the form that prohibited to create the
              account, please correct the errors shown below:
            </div>
          {/if}
          <input type="hidden" name="stripe_token" value={stripe_token} />
          <input
            type="hidden"
            name="stripe_product_id"
            value={stripe_product.stripe_id}
          />
          {#if coupon.id}
            <input type="hidden" name="stripe_coupon" value={coupon.id} />
          {/if}
          <input
            type="hidden"
            name="stripe_price_id"
            value={stripe_product.price_id}
          />

          <div class:hidden={totalAmount == 0}>
            <!-- We'll put the error messages in this element -->
            <div bind:this={cardErrors} role="alert"></div>
            <div class="mt-4 grid grid-cols-1 gap-y-4 gap-x-4">
              <div class="pb-2">
                <label
                  for="first_name"
                  class="block text-sm font-medium text-gray-700"
                >
                  Credit Card
                </label>
                <div
                  class="mt-1 p-2 border shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                >
                  <div id="card-element" class=""></div>
                  <input type="hidden" bind:this={cardTokenInput} />
                </div>
              </div>
            </div>
          </div>
          <div class="mt-8">
            <button
              type="submit"
              disabled={saving}
              class="btn big bg-green-600 hover:bg-green-700 text-white justify-center font-bold text-center w-full"
            >
              {saving ? "Please wait..." : "Complete Purchase"}
            </button>
          </div>
        </form>
      {:else}
        <p class="text-lg mt-12 text-gray-800">
          Thank you for your interest in purchasing the alignment test.
        </p>
        <p class="text-lg mt-8 text-gray-800">
          To get the details on purchasing an alignment test for your team
          please contact our sales teams at
          <a
            href="mailto:hello@leadwithpurpose.com"
            class="text-blue-600 hover:text-blue-700 underline"
            >hello@leadwithpurpose.com</a
          >
        </p>
      {/if}
    </div>
  </div>
</div>
